import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { MdNotificationsNone } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getData } from 'utils/httpClient';
import authService from 'services/authService';
import {
  CollapsedIconButton,
  CollapsedSidebarItem,
  collapsedIconInnerSx,
} from './CollapsedSidebarItem';
import { secondaryButtonProps } from '../../../theme/adminUi';
import { stockInventarioPath } from '../../../views/admin/stock/stockPaths';

const REFRESH_MS = 5 * 60 * 1000;
const POPUP_Z_INDEX = 9999;

/** false en .env hasta tener stock migrado: REACT_APP_STOCK_ALERTS_ENABLED=false */
const STOCK_ALERTS_ENABLED =
  process.env.REACT_APP_STOCK_ALERTS_ENABLED !== 'false';

export default function StockAlertsMenu({ collapsed = false }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const textColor = useColorModeValue('#003064', 'white');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const menuBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const itemBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const criticalBg = useColorModeValue('red.50', 'red.900');
  const criticalBorder = useColorModeValue('red.200', 'red.700');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );

  const canView =
    !authService.isMechanic() &&
    authService.constructor.checkPermissionFromStorage('products.view');

  const loadAlerts = useCallback(async () => {
    if (!STOCK_ALERTS_ENABLED || !canView) return;
    setLoading(true);
    try {
      const res = await getData('/stock/low-stock-alerts', { skipLoader: true });
      setAlerts(res?.payload?.alerts || []);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [canView]);

  useEffect(() => {
    loadAlerts();
    const timer = setInterval(loadAlerts, REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadAlerts]);

  if (!STOCK_ALERTS_ENABLED || !canView) {
    return null;
  }

  const count = alerts.length;
  const badgeLabel = count > 99 ? '99+' : String(count);

  const bellIcon = (
    <Box position="relative" display="inline-flex">
      <Icon as={MdNotificationsNone} boxSize={collapsed ? '22px' : '20px'} />
      {count > 0 && (
        <Badge
          position="absolute"
          top="-6px"
          right="-8px"
          colorScheme={alerts.some((a) => a.currentStock === 0) ? 'red' : 'orange'}
          borderRadius="full"
          fontSize="9px"
          minW="16px"
          h="16px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={1}
        >
          {badgeLabel}
        </Badge>
      )}
    </Box>
  );

  const menuButton = collapsed ? (
    <CollapsedSidebarItem>
      <MenuButton
        as={CollapsedIconButton}
        bg="transparent"
        color={textColor}
        title="Alertas de stock"
        aria-label="Alertas de stock"
        _hover={{ bg: hoverBg }}
        _active={{ bg: hoverBg }}
      >
        <Box as="span" sx={collapsedIconInnerSx} color="inherit">
          {bellIcon}
        </Box>
      </MenuButton>
    </CollapsedSidebarItem>
  ) : (
    <MenuButton
      as={IconButton}
      aria-label="Alertas de stock"
      icon={bellIcon}
      size="sm"
      variant="ghost"
      color={textColor}
      _hover={{ bg: hoverBg }}
      _active={{ bg: hoverBg }}
    />
  );

  return (
    <Menu placement="right-end" gutter={16} strategy="fixed" closeOnSelect={false}>
      {menuButton}
      <Portal>
        <MenuList
          zIndex={POPUP_Z_INDEX}
          p={0}
          w="320px"
          maxW="90vw"
          bg={menuBg}
          borderColor={borderColor}
          boxShadow={shadow}
          overflow="hidden"
        >
          <Box
            px={4}
            py={3}
            borderBottomWidth="1px"
            borderColor={borderColor}
            fontWeight="semibold"
            fontSize="sm"
          >
            Alertas de bajo stock
          </Box>

          {loading && !loadedOnce ? (
            <Flex justify="center" py={6}>
              <Spinner size="sm" />
            </Flex>
          ) : count === 0 ? (
            <Box px={4} py={5}>
              <Text fontSize="sm" color={mutedColor}>
                No hay productos bajo su umbral de alerta de stock.
              </Text>
            </Box>
          ) : (
            <VStack
              align="stretch"
              spacing={0}
              maxH="320px"
              overflowY="auto"
              py={1}
            >
              {alerts.map((item) => {
                const isCritical = item.currentStock === 0;
                return (
                  <Box
                    key={item.productId}
                    px={4}
                    py={2.5}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                    bg={isCritical ? criticalBg : itemBg}
                    borderLeftWidth={isCritical ? '3px' : 0}
                    borderLeftColor={isCritical ? criticalBorder : 'transparent'}
                  >
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                      {item.name}
                    </Text>
                    <Flex justify="space-between" align="center" mt={1} gap={2}>
                      <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                        {item.code || 'Sin código'}
                        {item.categoryName ? ` · ${item.categoryName}` : ''}
                        {` · alerta ≤ ${item.threshold}`}
                      </Text>
                      <Badge
                        colorScheme={isCritical ? 'red' : 'orange'}
                        fontSize="xs"
                        flexShrink={0}
                      >
                        {item.currentStock} u.
                      </Badge>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          )}

          <Flex px={3} py={3} gap={2} borderTopWidth="1px" borderColor={borderColor}>
            <Button
              {...secondaryButtonProps}
              size="sm"
              flex={1}
              onClick={loadAlerts}
              isLoading={loading}
            >
              Actualizar
            </Button>
            <Button
              size="sm"
              flex={1}
              colorScheme="gray"
              bg="#003064"
              color="white"
              _hover={{ bg: '#004080' }}
              onClick={() => navigate(stockInventarioPath('inventario'))}
            >
              Ver inventario
            </Button>
          </Flex>
        </MenuList>
      </Portal>
    </Menu>
  );
}
